import { Injectable, OnDestroy } from '@angular/core';
import { interval, Observable, Subject } from 'rxjs';
import { takeUntil, tap, debounceTime, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { LayerRenderer } from './models/layer-renderer.model';
import { Layer, LayerTypes, LayerDataChangeEvent, LayerDataChangeType, VisualizationLayerType } from 'src/app/modules/cop/store/cop/models';
import { MAX_DOCS_TO_RENDER, StandardLayerRenderer } from './layer-renderers/standard-layer.renderer';
import { AssetsFlightsLayerRenderer } from './layer-renderers/assets-flight-layer.renderer';
import { MapDrawingContext } from './models/map-drawing-context.model';
import { DatasetType, DatasetSubtype, Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { MapPositionService } from './map-position.service';
import { MapEventsService } from 'src/app/modules/cop/services/map-events.service';
import * as fromCop from 'src/app/modules/cop/store/cop';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { MapWithState } from 'src/app/modules/cop/store/cop';
import { FolderUtils } from '../services/map/folder-utils';
import { MapLayerQueryBuildService } from '../services/data/map-layer-query-build.service';
import { MapLayerClusterService } from './cluster.service';
import { Document, FieldDataType } from '../../core/store/models';
import LightTheme from 'src/app/modules/cop/drawing/themes/light.json';
import LightPoiTheme from 'src/app/modules/cop/drawing/themes/light_poi.json';
import BlueTheme from 'src/app/modules/cop/drawing/themes/blue.json';
import BluePoiTheme from 'src/app/modules/cop/drawing/themes/blue_poi.json';
import PaleTheme from 'src/app/modules/cop/drawing/themes/pale.json';
import PalePoiTheme from 'src/app/modules/cop/drawing/themes/pale_poi.json';
import DarkColorTheme from 'src/app/modules/cop/drawing/themes/dark_color.json';
import DarkColorPoiTheme from 'src/app/modules/cop/drawing/themes/dark_color_poi.json';
import DarkGrayTheme from 'src/app/modules/cop/drawing/themes/dark_gray.json';
import DarkGrayPoiTheme from 'src/app/modules/cop/drawing/themes/dark_gray_poi.json';
import DarkPurpleTheme from 'src/app/modules/cop/drawing/themes/dark_purple.json';
import DarkPurplePoiTheme from 'src/app/modules/cop/drawing/themes/dark_purple_poi.json';
import { AlertsPowerOutagesLayerRenderer } from './layer-renderers/alerts-power-outages-layer.renderer';
import { TopoTileLayerRenderer } from './layer-renderers/topo-tile-layer.renderer';
import MeasureTool from 'measuretool-googlemaps-v3/dist/gmaps-measuretool.esm';
import { Logger, LogLevel } from '../../core/services/logger/logger.model';
import { LoggerService } from '../../core/services/logger/logger.service';
import { TropicalCyclonesLayerRenderer } from './layer-renderers/tropical-cyclones-layer.renderer';
import { GeoJsonUtilityService } from './geo-json-utility.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertsFlightsLayerRenderer } from './layer-renderers/alerts-flights-layer.renderer';
import { NightLayerService } from './night-layer.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MapLabelService } from './label.service';
import { Feature } from './features/feature.model';
import { MapDataWorkerService } from '../web-workers/client/map-data-worker.service';
import { TravelerLayerRenderer } from './layer-renderers/traveler-layer.renderer';
import { AlertsEarthquakesLayerRenderer } from './layer-renderers/alerts-earthquakes-layer.renderer';
import { AlertsWildfireLayerRenderer } from './layer-renderers/alert-wildfires-layer.renderer';
import { MapLayerVisibilityService } from './visibility.service';
import { UserProfile } from 'src/app/modules/core/store/user-profile';
//import { AerisWeatherController } from './aeris-weather.controller';
import { WeatherTileLayerRenderer } from './layer-renderers/weather-tile-layer.renderer';
import { MapPolygonVisibilityService } from './map-polygon-visibility.service';
import { MapDrawingStateService } from './map-drawing-state.service';
import { AlertsPowerOutagesV2LayerRenderer } from '@app/modules/cop/drawing/layer-renderers/alerts-power-outages-v2-layer.renderer';
import { TrafficCamerasLayerRenderer } from './layer-renderers/traffic-cameras-layer.renderer';
import { AssetsSamsaraVehicleLayerRenderer } from './layer-renderers/assets-samsara-vehicle-layer.renderer';
import { AlertsSamsaraVehicleLayerRenderer } from './layer-renderers/alerts-samsara-vehicle-layer.renderer';
import { CameraGroupLayerRenderer } from '@app/modules/cop/drawing/layer-renderers/camera-group-layer.renderer';
import { PopupService } from './popups/popup.service';
import { VisualizationsHeatmapLayerRenderer } from './layer-renderers/visualizations-heatmap-layer.renderer';
import { HorizonGeospatialThreatZoneLayerRenderer } from './layer-renderers/horizon-geospatial-threat-zones-layer.renderer';
import moment from 'moment';
import { CameraLayerRenderer } from './layer-renderers/camera-layer.renderer';
import { POIDoorsLayerRenderer } from './layer-renderers/poi-doors-layer.renderer';
import { HorizonRatingsUtilsService } from '../components/horizon-ratings';
import { MapWallDisplayFacade } from '@app/features/operating-picture/map-wall-display';
import { DrawingToolsService } from './drawing-tools.service';
import { API_BASE_URL } from '@app/core/constants';
import { TransportsRenderer } from './layer-renderers/transports-layer.renderer';

const DEFAULT_ZOOM_LEVEL = 4;
const DEFAULT_POSITION = { lat: 40.4682, lng: -98.560224 };

// whether render/create map elements only if they are visible
const RENDER_ONLY_VISIBLE = true;

/**
 * The MapDrawingService is the main controller of the map rendering behavior. It
 * will maintain the state of drawn items and handle layer change events combined
 * with the current map state.
 *
 * LayerRenderers are registered below in the "layerRenderers" member variable. The behavior for dataset based layers is to
 * return a renderer based on the DatasetType-DatasetSubtype. If that does not match
 * then it will fallback to DatasetType. If that name is not found then it will fall back
 * use "standard" for the standard pin renderer. This design lets a developer fully customize
 * the pin rendering process if needed.
 */
@Injectable({
  providedIn: 'root'
})
export class MapDrawingService implements OnDestroy {
  googleGeocoder: google.maps.Geocoder;
  userProfile: UserProfile;
  renderOnlyVisible: boolean = RENDER_ONLY_VISIBLE;
  destroy$ = new Subject<void>();
  layerRenderers: { [name: string]: any } = {
    'Alerts-PowerOutage': new AlertsPowerOutagesLayerRenderer(),
    'Alerts-PowerOutageV2': new AlertsPowerOutagesV2LayerRenderer(),
    'Assets-Flight': new AssetsFlightsLayerRenderer(),
    'Alerts-Flight': new AlertsFlightsLayerRenderer(),
    'Alerts-TropicalCyclone': new TropicalCyclonesLayerRenderer(this.translateService),
    'Assets-TravelItinerary': new TravelerLayerRenderer(),
    'Alerts-Earthquake': new AlertsEarthquakesLayerRenderer(),
    'standard': new StandardLayerRenderer(),
    'Alerts-Wildfire': new AlertsWildfireLayerRenderer(),
    'Assets-Vehicle': new AssetsSamsaraVehicleLayerRenderer(),
    'Alerts-Vehicle': new AlertsSamsaraVehicleLayerRenderer(),
    'Camera-Traffic': new TrafficCamerasLayerRenderer(),
    'Camera-Group': new CameraGroupLayerRenderer(),
    'Visualizations-Heatmap': new VisualizationsHeatmapLayerRenderer(),
    'Alerts-HorizonGeospatialThreatZone': new HorizonGeospatialThreatZoneLayerRenderer(),
    'Camera-Camera': new CameraLayerRenderer(),
    'PointOfInterest-Door': new POIDoorsLayerRenderer(),
    'Assets-Transports': new TransportsRenderer()
  };

  isMapMoving: boolean = false;
  isMapUnloading: boolean = true;
  options: any = {};
  mapContainerId: string = 'topoCop';
  locationSearchMarkers: google.maps.Marker[] = [];
  currentlyDrawing: boolean;
  currentDrawingTool: string;
  drawToolAllShapes$: Observable<any>;
  //aerisWeatherController = new AerisWeatherController();
  enhancedWeatherLayersEnabled: boolean;
  currentTimelineDate: any = null;
  floor: string;
  logger: Logger;

  public measureTool: MeasureTool;
  private trafficLayer: google.maps.TrafficLayer;
  private transitLayer: google.maps.TransitLayer;

  public visibilityService: MapLayerVisibilityService;

  private refreshDocuments$: Subject<{
    layerRenderer: LayerRenderer;
    drawingContext: MapDrawingContext;
    layer: Layer;
    documents?: Document[];
  }> = new Subject();

  constructor(
    private mapDrawingStateService: MapDrawingStateService,
    private store$: Store,
    private mapEventsService: MapEventsService,
    private clusterService: MapLayerClusterService,
    private labelService: MapLabelService,
    private drawingTools: DrawingToolsService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
    private mapPositionService: MapPositionService,
    private nightLayerService: NightLayerService,
    private spinnerService: NgxSpinnerService,
    private workerService: MapDataWorkerService,
    private mapPolygonVisibilityService: MapPolygonVisibilityService,
    private popupService: PopupService,
    private ratingsUtils: HorizonRatingsUtilsService,
    private wallDisplay: MapWallDisplayFacade
  ) {
    this.logger = this.loggerService.getLogger('MapDrawingService');

    this.visibilityService = new MapLayerVisibilityService(
      this,
      this.mapPositionService,
      this.clusterService,
      this.labelService,
      this.workerService,
      this.logger,
      this.mapPolygonVisibilityService
    );

    // track map state changes and look for visibility updates
    this.store$
      .select(fromCop.selectSelectedMapState)
      .pipe(
        withLatestFrom(
          this.store$.select(fromCop.selectIsMapLoading),
          this.store$.select(fromUserProfile.selectProfileUser),
          this.store$.select(fromUserProfile.selectOrganization)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([mapState, isMapLoading, userProfile, organization]) => {
        this.mapDrawingStateService.mapState = mapState;
        this.userProfile = userProfile;
        this.enhancedWeatherLayersEnabled = organization?.mapSettings?.enhancedWeatherLayersEnabled;

        try {
          if (typeof google === undefined || typeof google.maps === undefined) {
            return;
          }
        } catch (e) {
          return;
        }

        if (!!!this.googleGeocoder) {
          this.googleGeocoder = new google.maps.Geocoder();
        }

        if (!isMapLoading && (this.wallDisplay.isActive() || !this.isMapUnloading)) {
          this.scanForVisibilityChanges(false);
          this.clusterService.setLayerSettings(
            mapState,
            this.mapEventsService,
            this,
            this.mapDrawingStateService.googleMap,
            this.popupService
          );
        }
      });

    this.store$
      .select(fromUserProfile.selectDistanceUnit)
      .pipe(takeUntil(this.destroy$))
      .subscribe(distanceUnit => {
        if (!!this.measureTool) {
          const unit = distanceUnit?.toLowerCase() === 'miles' ? 'Imperial' : 'Metric';
          this.measureTool.setOption('unit', unit);
        }
      });

    // every 3 minutes cause the tiles to refresh in case they need to
    // show new data
    const threeMinutes = 180000;
    interval(threeMinutes)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.bustOverlayCache();
        })
      )
      .subscribe();

    this.refreshDocuments$
      .pipe(
        takeUntil(this.destroy$),
        tap(data => this.processRefreshDocumentsChunk(data.layerRenderer, data.drawingContext, data.layer, data.documents))
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.complete();
    this.destroy$ = null;
    this.mapDrawingStateService.googleMap.unbindAll();
    this.mapDrawingStateService.googleMap = null;
  }

  initMap(mapContainerId: string, options?: any) {
    try {
      if (typeof google === undefined || typeof google.maps === undefined) {
        return;
      }
    } catch (e) {
      return;
    }
    this.mapDrawingStateService.isInitializing.next(true);
    this.logger?.logInfo('initMap', {});

    const container = document.getElementById(mapContainerId);

    if (!container) {
      return;
    }

    const zoom = options?.zoom || DEFAULT_ZOOM_LEVEL;
    const position = options?.position || DEFAULT_POSITION;

    this.mapContainerId = mapContainerId;
    this.options = options;
    this.mapDrawingStateService.mapState = {} as MapWithState;
    this.visibilityService?.init();
    this.mapDrawingStateService.mapFeatures = {};
    this.trafficLayer = null;
    this.transitLayer = null;

    this.measureTool = null;
    this.mapDrawingStateService.googleMap = null;

    this.mapDrawingStateService.googleMap = new google.maps.Map(container, {
      tilt: 0,
      center: position,
      zoom,
      gestureHandling: 'greedy',
      mapTypeControl: false,
      cameraControl: false,
      streetViewControl: true,
      streetViewControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      zoomControl: false,
      fullscreenControl: false,
      rotateControl: false,
      scaleControl: true,
      clickableIcons: false,
      disableDoubleClickZoom: true,
      minZoom: 2,
      maxZoom: 20,
      mapId: 'TOPO_COP'
    });

    this.mapDrawingStateService.googleMap.getStreetView().setOptions({
      addressControlOptions: {
        position: google.maps.ControlPosition.BOTTOM_CENTER
      }
    });

    this.trafficLayer = new google.maps.TrafficLayer();
    this.transitLayer = new google.maps.TransitLayer();

    // load the map themes
    this.mapDrawingStateService.googleMap.mapTypes.set('dark-gray', new google.maps.StyledMapType(DarkGrayTheme, { name: 'dark-gray' }));
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'dark-gray-poi',
      new google.maps.StyledMapType(DarkGrayPoiTheme, { name: 'dark-gray-poi' })
    );
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'dark-purple',
      new google.maps.StyledMapType(DarkPurpleTheme, { name: 'dark-purple' })
    );
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'dark-purple-poi',
      new google.maps.StyledMapType(DarkPurplePoiTheme, { name: 'dark-purple-poi' })
    );
    this.mapDrawingStateService.googleMap.mapTypes.set('dark-color', new google.maps.StyledMapType(DarkColorTheme, { name: 'dark-color' }));
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'dark-color-poi',
      new google.maps.StyledMapType(DarkColorPoiTheme, { name: 'dark-color-poi' })
    );
    this.mapDrawingStateService.googleMap.mapTypes.set('light-gray', new google.maps.StyledMapType(LightTheme, { name: 'light-gray' }));
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'light-gray-poi',
      new google.maps.StyledMapType(LightPoiTheme, { name: 'light-gray-poi' })
    );
    this.mapDrawingStateService.googleMap.mapTypes.set('light-pale', new google.maps.StyledMapType(PaleTheme, { name: 'light-pale' }));
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'light-pale-poi',
      new google.maps.StyledMapType(PalePoiTheme, { name: 'light-pale-poi' })
    );
    this.mapDrawingStateService.googleMap.mapTypes.set('light-blue', new google.maps.StyledMapType(BlueTheme, { name: 'light-blue' }));
    this.mapDrawingStateService.googleMap.mapTypes.set(
      'light-blue-poi',
      new google.maps.StyledMapType(BluePoiTheme, { name: 'light-blue-poi' })
    );

    // hook the map events
    this.mapDrawingStateService.googleMap.addListener('bounds_changed', () => {
      this.mapEventsService.onMapBoundsChanged(this.visibilityService?.getVisibleLayerUIds());
      this.popupService.close();
    });
    this.mapDrawingStateService.googleMap.addListener('dragstart', () => this.onDragStart());
    this.mapDrawingStateService.googleMap.addListener('dragend', () => this.onDragEnd());
    this.mapDrawingStateService.googleMap.addListener('zoom_changed', () => this.mapEventsService.onMapZoomChanged());
    this.mapDrawingStateService.googleMap.addListener('click', e => this.mapEventsService.onMapClick(e));
    this.mapDrawingStateService.googleMap.getStreetView().addListener('visible_changed', () => this.mapEventsService.onStreetViewChanged());

    this.mapEventsService.mapZoomChanged$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        tap(() => {
          this.scanForVisibilityChanges(true);
          this.scanForLabelChanges();
        })
      )
      .subscribe();

    this.mapEventsService.timelineDateChanged$
      .pipe(
        takeUntil(this.destroy$),
        tap(dateValue => {
          this.currentTimelineDate = moment(dateValue);

          const timelineLayers = this.mapDrawingStateService?.mapState?.layers?.filter(l => l.displayOptions?.showInTimeline);
          if (timelineLayers) {
            timelineLayers.map(l => this.visibilityService.applyDatasetLayerVisibilityChanges(l));
          }
        })
      )
      .subscribe();

    this.measureTool = new MeasureTool(this.mapDrawingStateService.googleMap, {
      showSegmentLength: true,
      unit: this.userProfile?.culture?.distanceUnit.toLowerCase() === 'miles' ? 'Imperial' : 'Metric',
      contextMenu: false
    });

    this.measureTool.addListener('measure_start', () => {
      this.setClickForLayerShapesWhileDrawing(false);
    });

    // callback when measure tool ended, reestablish polygons and clusters as clickable
    this.measureTool.addListener('measure_end', () => {
      this.setClickForLayerShapesWhileDrawing(true);
    });

    this.mapDrawingStateService.googleMap.addListener('rightclick', event => {
      // this triggers the Measure tool "contextmenu" listener
      google.maps.event.trigger(this.mapDrawingStateService.googleMap, 'contextmenu', event);
    });

    this.clusterService.initCluster();

    this.popupService.setMap(this.mapDrawingStateService.googleMap);

    this.mapDrawingStateService.googleMap.addListener('projection_changed', () => {
      this.drawingTools.init(this.mapDrawingStateService.googleMap);
    });

    this.mapDrawingStateService.isInitializing.next(false);
  }

  private scanForLabelChanges() {
    if (this.isMapMoving) {
      setTimeout(() => {
        this.scanForLabelChanges();
      }, 250);
      return;
    }

    Object.keys(this.mapDrawingStateService.mapFeatures).forEach(layerUId => {
      if (this.visibilityService?.isLayerVisible(layerUId) === true && !!this.mapDrawingStateService.mapState?.layersSet) {
        const layer = this.mapDrawingStateService.mapState.layersSet[layerUId];

        if (!!layer && layer.isActive === true && layer.displayOptions?.labelsVisible === true) {
          this.labelService.checkLayerLabelsDisplay(layerUId);
        }
      }
    });
  }

  // visibility
  private scanForVisibilityChanges(forZoom: boolean): void {
    if (this.isMapMoving) {
      setTimeout(() => {
        this.scanForVisibilityChanges(forZoom);
      }, 250);
      return;
    }

    var visibleLayerUIds = this.visibilityService?.getVisibleLayerUIds();
    this.visibilityService?.scanForVisibilityChanges(this.mapDrawingStateService.mapState, this.mapDrawingStateService.mapFeatures);

    // check if need to perform bounds query after visibility has changed
    var newVisibleLayerUIds = this.visibilityService?.getVisibleLayerUIds();

    for (let layerUId of newVisibleLayerUIds) {
      if (!visibleLayerUIds.includes(layerUId)) {
        const layer = this.mapDrawingStateService.mapState?.layersSet[layerUId];
        if (!!layer) {
          const dataset = !!this.mapDrawingStateService.mapState?.datasetsSet
            ? this.mapDrawingStateService.mapState?.datasetsSet[layer.datasetUId]
            : null;
          if (MapLayerQueryBuildService.isUsingBoundingBoxUpdateQuery(layer, dataset)) {
            this.mapEventsService.onMapBoundsChanged(this.visibilityService?.getVisibleLayerUIds());
            break;
          }
        }
      }
    }
  }

  private isDocumentVisible(layer: Layer, document: Document): boolean {
    return this.visibilityService?.isDocumentVisible(layer, document);
  }

  private onLoadingDone(layer: Layer): void {
    this.setLoadingDone(layer.uId);
  }

  private setLoadingDone(layerUId: string): void {
    setTimeout(() => {
      this.store$.dispatch(fromCop.setLayerLoadingOffToStore({ layerUId: layerUId, isError: false }));
    }, 100);
  }

  public processDocumentVisibilityChanges(document: Document, layer: Layer, forceLayerIsVisible: boolean = null) {
    this.visibilityService?.processDocumentVisibilityChanges(document, layer, this.mapDrawingStateService.mapState, forceLayerIsVisible);
  }

  public redrawAfterEditingGeo(document: Document, layer: Layer) {
    if (!!!layer) {
      return;
    }

    const visibilityState = this.visibilityService.isLayerVisible(layer.uId);
    this.visibilityService.processDocumentVisibilityChanges(document, layer, this.mapDrawingStateService.mapState, visibilityState);
  }

  // click events
  processDatasetLayerClickChanges(layer: Layer, layerClick: boolean): void {
    const layerFeatures = this.mapDrawingStateService.mapFeatures[layer.uId];
    if (!!!layerFeatures) {
      return;
    }

    const docUIds = Object.keys(layerFeatures);
    for (let docUId of docUIds) {
      const docFeatures = layerFeatures[docUId];
      if (!!!docFeatures) {
        continue;
      }

      for (let circle of docFeatures.circles) {
        circle.setOptions({ clickable: layerClick });
      }

      for (let marker of docFeatures.markers) {
        marker.setOptions({ clickable: layerClick });
      }

      for (let polygon of docFeatures.polygons) {
        polygon.setOptions({ clickable: layerClick });
      }

      for (let polyline of docFeatures.polylines) {
        polyline.setOptions({ clickable: layerClick });
      }

      for (let rectangle of docFeatures.rectangles) {
        rectangle.setOptions({ clickable: layerClick });
      }
    }
  }

  // drag and drop
  onDragStart() {
    this.isMapMoving = true;
  }

  onDragEnd() {
    this.isMapMoving = false;
  }

  bustOverlayCache(): void {
    if (!this.mapDrawingStateService.googleMap) {
      return;
    }

    // this will change the zoom of the map
    this.mapDrawingStateService.googleMap.setZoom(this.mapDrawingStateService.googleMap.getZoom() + 0.000000000000001);

    // this will change the zoom again and load fresh tiles
    this.mapDrawingStateService.googleMap.setZoom(Math.round(this.mapDrawingStateService.googleMap.getZoom()));
  }

  // deletes all map resources
  public clearFeatures(): void {
    this.nightLayerService.hide();
    this.labelService.deleteAll();
    if (!!this.mapDrawingStateService.mapFeatures) {
      Object.keys(this.mapDrawingStateService.mapFeatures).forEach(l => {
        if (!!this.mapDrawingStateService.mapState?.layersSet && !!this.mapDrawingStateService.mapState.layersSet[l]) {
          const layer = this.mapDrawingStateService.mapState.layersSet[l];
          const drawingContext = this.getMapDrawingContext(layer);
          drawingContext.clusterService.removeLayerClusters(drawingContext, layer);
          drawingContext.clusterService.deleteLayerCluster(layer.uId);
          const layerRenderer = this.getLayerRenderer(this.mapDrawingStateService.mapState, layer);
          if (!!layerRenderer) {
            const layerFeatures = drawingContext.mapFeatures[layer.uId];
            if (!!layerFeatures) {
              const docUIds = Object.keys(layerFeatures);
              for (let uid of docUIds) {
                layerRenderer.removeDocument(drawingContext, layer, uid, layerFeatures);
              }
            }
          }
          this.clusterService.clearChanges(layer.uId);
        }
      });
      this.mapDrawingStateService.mapFeatures = {};
    }
    this.visibilityService?.init();
    this.clusterService?.initCluster();
    this.spinnerService.hide('map');
  }

  public clearAll(options?: any): void {
    try {
      if (typeof google === undefined || typeof google.maps === undefined) {
        return;
      }
    } catch (e) {
      return;
    }

    this.isMapUnloading = true;
    this.clearFeatures();
    this.mapDrawingStateService.googleMap?.unbindAll();
    this.initMap(this.mapContainerId, options || this.options);
    this.isMapUnloading = false;
  }

  public clearWithoutInit(): void {
    try {
      if (typeof google === undefined || typeof google.maps === undefined) {
        return;
      }
    } catch (e) {
      return;
    }

    this.isMapUnloading = true;
    this.clearFeatures();
    this.mapDrawingStateService.googleMap?.unbindAll();
    this.mapDrawingStateService.googleMap = null;
    this.isMapUnloading = false;
  }

  public setCenterAndZoom(lat: number, lng: number, zoomLevel: number): void {
    if (!this.mapDrawingStateService.googleMap) {
      return;
    }

    const currentMapZoom = this.mapDrawingStateService.googleMap.getZoom();

    if (currentMapZoom !== zoomLevel) {
      this.mapDrawingStateService.googleMap.setZoom(zoomLevel > 20 ? 20 : zoomLevel);
    }
    this.mapDrawingStateService.googleMap.setCenter(new google.maps.LatLng(lat, lng));
  }

  public fitToBounds(bounds: any): void {
    this.mapDrawingStateService.googleMap.fitBounds(bounds);
  }

  public getZoom(): number {
    return this.mapDrawingStateService.googleMap.getZoom();
  }

  public setZoom(zoom: number): void {
    this.mapDrawingStateService.googleMap.setZoom(zoom);
  }

  public showNightLayer() {
    this.nightLayerService.show(this.mapDrawingStateService.googleMap);
  }

  public hideNightLayer() {
    this.nightLayerService.hide();
  }

  processLayerChangeEvent(event: LayerDataChangeEvent): void {
    if (event.type === LayerDataChangeType.Add) {
      this.processAddEvent(event);
    } else if (event.type === LayerDataChangeType.Remove) {
      this.processRemoveEvent(event);
    } else if (event.type === LayerDataChangeType.Update) {
      this.processUpdateEvent(event);
    }
  }

  processRemoveEvent(event: LayerDataChangeEvent): void {
    const { layerUId } = event;

    const documents = Object.values(event.changes ?? {}) as Document[];

    this.removeLayerDocuments(layerUId, documents);
  }

  removeLayerDocuments(layerUId: string, documents?: Document[], forceRenderLocal?: boolean) {
    if (!!!this.mapDrawingStateService || !!!this.mapDrawingStateService?.mapState || !!!this.mapDrawingStateService?.mapState?.layersSet) {
      return;
    }

    const layer = this.mapDrawingStateService?.mapState?.layersSet[layerUId];
    if (!!!layer || layer.isActive !== true) {
      return;
    }

    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'removeLayerDocuments()', {
      layerUId: layer.uId,
      layerName: layer?.name,
      documentCount: documents?.length ?? 0
    });

    const layerRenderer = this.getLayerRenderer(this.mapDrawingStateService.mapState, layer, forceRenderLocal);
    if (!!!layerRenderer) {
      stopwatch.stop();
      return;
    }

    const layerState = this.mapDrawingStateService.mapState.layerStates[layer.uId];

    if (!!layerState) {
      const drawingContext = this.getMapDrawingContext(layer);
      const documentUIds = !!documents ? documents.map(d => d.uid) : Object.keys(layerState.documents);
      layerRenderer.removeDocuments(drawingContext, layer, documentUIds);
    }

    stopwatch.stop();
  }

  processAddEvent(event: LayerDataChangeEvent): void {
    const { layerUId } = event;

    const documents = Object.values(event.changes ?? {}) as Document[];

    if (!!!this.mapDrawingStateService.mapState?.layersSet) {
      this.setLoadingDone(layerUId);
      return;
    }

    const layer = this.mapDrawingStateService.mapState?.layersSet[layerUId];

    if (!!!layer || layer.isActive !== true) {
      this.setLoadingDone(layerUId);
      return;
    }

    // add only visible documents ?
    const documentsToAdd = this.renderOnlyVisible === true ? this.visibilityService.getVisibleDocuments(layer, documents) : documents;

    this.addLayerDocuments(layer, documentsToAdd, event.floor);
  }

  addLayerDocuments(layer: Layer, documents: Document[], floor?: string, forceRenderLocal?: boolean) {
    if (this.isLayerUsingDocuments(layer) && documents.length == 0) {
      this.setLoadingDone(layer.uId);
      return;
    }

    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'addLayerDocuments()', {
      layerUId: layer.uId,
      layerName: layer?.name,
      documentCount: documents?.length ?? 0
    });

    const layerRenderer = this.getLayerRenderer(this.mapDrawingStateService.mapState, layer, forceRenderLocal);
    if (!!!layerRenderer) {
      stopwatch.stop();
      return;
    }
    const drawingContext = this.getMapDrawingContext(layer);

    layerRenderer.addDocuments(drawingContext, layer, documents, floor);

    this.mapPolygonVisibilityService.processMapMoveForPolygonVisibility([layer.uId]);

    stopwatch.stop();
  }

  processUpdateEvent(event: LayerDataChangeEvent): void {
    const { layerUId } = event;

    const documents = Object.values(event.changes ?? {}) as Document[];

    if (!!!this.mapDrawingStateService.mapState?.layersSet) {
      this.setLoadingDone(layerUId);
      return;
    }

    const layer = this.mapDrawingStateService.mapState?.layersSet[layerUId];

    if (!!!layer || layer.isActive !== true) {
      this.setLoadingDone(layerUId);
      return;
    }

    const dataset = this.mapDrawingStateService.mapState?.datasetsSet[layer.datasetUId];
    if (!!dataset?.useTileServer) {
      this.setLoadingDone(layerUId);
      return;
    }

    // update only visible documents ?
    const documentsToUpdate = this.renderOnlyVisible === true ? this.visibilityService.getVisibleDocuments(layer, documents) : documents;

    this.updateLayerDocuments(layer, documentsToUpdate);

    this.mapPolygonVisibilityService.processMapMoveForPolygonVisibility([layer.uId]);

    const documentsToRemove = documents.filter(d => !documentsToUpdate.find(updatedDocument => updatedDocument.uId === d.uId));

    this.removeLayerDocuments(layer.uId, documentsToRemove);
  }

  updateLayerDocuments(layer: Layer, documents: Document[]) {
    if (this.isLayerUsingDocuments(layer) && documents.length == 0) {
      this.setLoadingDone(layer.uId);
      return;
    }

    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'updateLayerDocuments()', {
      layerUId: layer.uId,
      layerName: layer?.name,
      documentCount: documents?.length ?? 0
    });

    const layerRenderer = this.getLayerRenderer(this.mapDrawingStateService.mapState, layer);
    if (!!!layerRenderer) {
      stopwatch.stop();
      return;
    }
    const drawingContext = this.getMapDrawingContext(layer);

    layerRenderer.updateDocuments(drawingContext, layer, documents);

    stopwatch.stop();
  }

  // refresh
  refreshLayerDocuments(layerUId: string, documentUIds?: string[]) {
    const layer = this.mapDrawingStateService.mapState.layersSet[layerUId];

    if (!!!layer || layer.isActive !== true) {
      this.setLoadingDone(layerUId);
      return;
    }

    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'refreshLayerDocuments()', { layerUId });

    const layerRenderer = this.getLayerRenderer(this.mapDrawingStateService.mapState, layer);
    if (!!!layerRenderer) {
      stopwatch.stop();
      return;
    }
    const layerState = this.mapDrawingStateService.mapState.layerStates[layer.uId];

    if (!!layerState) {
      const documents = !!documentUIds
        ? Object.values(layerState.documents).filter(d => documentUIds.includes(d.uid))
        : Object.values(layerState.documents);

      const drawingContext = this.getMapDrawingContext(layer);

      const documentsToUpdate = this.renderOnlyVisible === true ? this.visibilityService.getVisibleDocuments(layer, documents) : documents;

      for (let i = 0; i < documentsToUpdate.length; i += MAX_DOCS_TO_RENDER) {
        const documentsChunk = documentsToUpdate.slice(i, i + MAX_DOCS_TO_RENDER);

        setTimeout(
          () =>
            this.refreshDocuments$.next({
              layerRenderer,
              drawingContext,
              layer,
              documents: documentsChunk
            }),
          0
        );
      }
    }

    stopwatch.stop();
  }

  refreshLayerDocumentLabels(layerUId: string, documentUIds?: string[]) {
    const layer = this.mapDrawingStateService.mapState.layersSet[layerUId];

    if (!!!layer || layer.isActive !== true) {
      return;
    }

    const showLabels = layer.displayOptions?.labelsVisible === true;

    const layerFeatures = this.mapDrawingStateService.mapFeatures[layer.uId];
    if (!!!layerFeatures) {
      return;
    }

    const docUIds = documentUIds || Object.keys(layerFeatures);

    for (let docUId of docUIds) {
      const docFeatures = layerFeatures[docUId];
      if (!!!docFeatures) {
        continue;
      }

      let feature: Feature = null;
      if (docFeatures.features?.length > 0) {
        feature = docFeatures.features[0];
        if (showLabels === true) {
          feature.showLabel();
        } else {
          feature.hideLabel();
        }
      }
    }
  }

  processRefreshDocumentsChunk(
    layerRenderer: LayerRenderer,
    drawingContext: MapDrawingContext,
    layer: Layer,
    documents?: Document[]
  ): void {
    const documentUIds = documents?.map(d => d.uid);
    layerRenderer.removeDocuments(drawingContext, layer, documentUIds);
    layerRenderer.addDocuments(drawingContext, layer, documents);
    this.visibilityService.applyDatasetLayerVisibilityChanges(layer, documentUIds);
  }

  markDocumentSelected(layerUId: string, documentUId: string, document: Document): void {
    const mapState = this.mapDrawingStateService.mapState;

    if (!mapState?.layersSet) {
      return;
    }
    const layer = mapState.layersSet[layerUId];
    const datasetUId = layer?.datasetUId;

    // check to see if this is a dataset based layer that is displayed using the tile server
    // if so then we need to draw the selected document so that we can see that it is highlighted on the map
    let isTileDocument = false;
    if (!!layer && !!datasetUId) {
      const dataset = mapState.datasetsSet[datasetUId];
      if (dataset.useTileServer) {
        isTileDocument = true;
        this.addLayerDocuments(layer, [document], null, true);
      }
    }

    const layerFeatures = this.mapDrawingStateService.mapFeatures[layerUId];
    if (!layerFeatures) {
      return;
    }

    const historyTrackEnabled = layer?.displayOptions?.historyTrackEnabled || document?.__historyTrackEnabled;

    setTimeout(() => {
      const docFeatures = layerFeatures[documentUId];
      if (!docFeatures) {
        return;
      }

      for (let feature of docFeatures.features ?? []) {
        feature.setSelected({
          isTileDocument,
          historyTrackEnabled,
          ...(historyTrackEnabled
            ? {
                historyTrackData: document?.__history
              }
            : {})
        });
      }
    });
  }

  markDocumentNormal(layerUId: string, documentUId: string, document: Document): void {
    const mapState = this.mapDrawingStateService.mapState;
    const layer = mapState.layersSet[layerUId];
    const datasetUId = layer?.datasetUId;

    // check to see if this is a dataset based layer that is displayed using the tile server
    // if so then we need to draw the selected document so that we can see that it is highlighted on the map
    if (!!layer && !!datasetUId) {
      const dataset = mapState.datasetsSet[datasetUId];
      if (dataset.useTileServer) {
        this.removeLayerDocuments(layerUId, [document], true);
        return;
      }
    }

    const layerFeatures = this.mapDrawingStateService.mapFeatures[layerUId];
    if (!layerFeatures) {
      return;
    }

    const docFeatures = layerFeatures[documentUId];
    if (!docFeatures) {
      return;
    }

    for (let feature of docFeatures.features ?? []) {
      feature.setUnselected();
    }
  }

  public isLayerUsingDocuments(layer: Layer): boolean {
    if (layer.type === LayerTypes.WeatherImagery) {
      return false;
    }
    const dataset = !!this.mapDrawingStateService.mapState?.datasetsSet
      ? this.mapDrawingStateService.mapState?.datasetsSet[layer.datasetUId]
      : null;
    return dataset?.useTileServer !== true;
  }

  // click events
  setClickForLayerShapesWhileDrawing(clickEnabled: boolean) {
    if (!!!this.mapDrawingStateService.mapState?.layers) {
      return;
    }

    for (let layer of this.mapDrawingStateService.mapState?.layers) {
      this.processDatasetLayerClickChanges(layer, clickEnabled);
    }
  }

  getLayerRenderer(mapState: MapWithState, layer: Layer, forceRenderLocal?: boolean): LayerRenderer {
    if (layer.type === LayerTypes.WeatherImagery) {
      // due to how map drawing service is root injectable, had to circumvent checks and initialization by making the weather renderer initialize from a component (MapWeatherLayerRendererComponent)
      // this is due to XWeather MapsGL integration charging based on sessions (5 minute intervals)
      if (this.enhancedWeatherLayersEnabled) {
        return;
      }

      // vvvv remove this once we are ready to get rid of Raster API weather renderer
      return new WeatherTileLayerRenderer();
    }

    let layerRenderer: LayerRenderer;

    // check to see if the layer is a dataset based type
    if (!!layer?.datasetUId) {
      layerRenderer = this.buildDatasetLayerRenderer(mapState, layer, forceRenderLocal);
      this.layerRenderers[layer.uId] = layerRenderer;
    }

    return layerRenderer;
  }

  buildDatasetLayerRenderer(mapState: MapWithState, layer: Layer, forceRenderLocal?: boolean): LayerRenderer {
    const dataset = mapState.datasetsSet[layer.datasetUId];

    if (!!!dataset) {
      return null;
    }

    if (!forceRenderLocal && dataset?.useTileServer === true) {
      return new TopoTileLayerRenderer();
    }

    if (layer.type === LayerTypes.Visualization) {
      const visualizationLayerType = VisualizationLayerType[layer?.displayOptions?.visualizationLayerOptions?.visualizationType] ?? null;
      const visualizationLayerRenderer = this.layerRenderers[`Visualizations-${visualizationLayerType}`] ?? null;
      return visualizationLayerRenderer;
    }

    const datasetLayerRendererNames = [
      `${DatasetType[dataset.type]}-${DatasetSubtype[dataset.subtype]}`,
      `${DatasetType[dataset.type]}`,
      'standard'
    ];

    for (let rendererName of datasetLayerRendererNames) {
      if (!!this.layerRenderers[rendererName]) {
        return this.layerRenderers[rendererName];
      }
    }

    return null;
  }

  getMapDrawingContext(layer: Layer): MapDrawingContext {
    const dataset = this.mapDrawingStateService.mapState.datasetsSet[layer?.datasetUId];
    const labelFields = !!dataset ? MapLayerQueryBuildService.getDatasetLabelFields(dataset) : null;

    const isLocationIntelRatingsSection = layer.type === LayerTypes.HorizonLocationIntelRatings;

    return {
      googleMap: this.mapDrawingStateService.googleMap,
      mapState: this.mapDrawingStateService.mapState,
      mapFeatures: this.mapDrawingStateService.mapFeatures,
      clusterService: this.clusterService,
      labelService: this.labelService,
      mapPosition: this.mapPositionService.position,
      mapEvents: this.mapEventsService,
      cacheBuster: new Date().getTime(),
      isWorkflowLayerSection: this.isWorkflowLayerSection(layer),
      isLocationIntelRatingsSection,
      ratingsUtils: isLocationIntelRatingsSection ? this.ratingsUtils : null,
      labelFields: labelFields,
      inCluster: this.clusterService.hasLayerCluster(layer.uId),
      isDocumentVisible: this.isDocumentVisible.bind(this),
      onLoadingDone: this.onLoadingDone.bind(this),
      isLayerVisible: this.visibilityService?.isLayerVisible(layer.uId),
      userProfile: this.userProfile,
      popup: this.popupService
    };
  }

  geocodeResult(query: string) {
    this.googleGeocoder.geocode({ address: query }, (results, status) => {
      if (status === 'OK') {
        this.zoomToSearchLocation(results[0].geometry.location, results[0].geometry.viewport);
      } else {
        console.error('Geocode was not successful: ' + status);
      }
    });
  }

  zoomToSearchLocation(location: google.maps.LatLng, viewport?: google.maps.LatLngBounds, zoomLevel?: number) {
    if (!!viewport) {
      this.mapDrawingStateService.googleMap.setCenter(location);
      this.mapDrawingStateService.googleMap.fitBounds(viewport);
    } else {
      this.setCenterAndZoom(location.lat(), location.lng(), zoomLevel ?? 15);
    }
    this.deleteLocationSearchMarkers();
    this.addLocationSearchMarker(location);
  }

  deleteLocationSearchMarkers() {
    this.locationSearchMarkers.forEach(marker => {
      google.maps.event.clearInstanceListeners(marker);
      marker.unbindAll();
      marker.setMap(null);
    });
    this.locationSearchMarkers = [];
  }

  addLocationSearchMarker(location: google.maps.LatLng) {
    const searchMarker = new google.maps.Marker({
      position: { lat: location.lat(), lng: location.lng() },
      icon: {
        url: `${API_BASE_URL}/v1/meta/markers/pin-teardrop-small/7257FF/0/0/normal/search_location.png`
      },
      visible: true,
      zIndex: 10000,
      map: this.mapDrawingStateService.googleMap
    });

    searchMarker.addListener('click', () => {
      this.deleteLocationSearchMarkers();
    });

    this.locationSearchMarkers.push(searchMarker);
  }

  showTraffic(): void {
    if (!!!this.mapDrawingStateService.googleMap) {
      return;
    }

    this.trafficLayer.setMap(this.mapDrawingStateService.googleMap);
  }

  hideTraffic(): void {
    if (!!!this.mapDrawingStateService.googleMap) {
      return;
    }

    this.trafficLayer.setMap(null);
  }

  showTransit(): void {
    if (!!!this.mapDrawingStateService.googleMap) {
      return;
    }

    this.transitLayer.setMap(this.mapDrawingStateService.googleMap);
  }

  hideTransit(): void {
    if (!!!this.mapDrawingStateService.googleMap) {
      return;
    }

    this.transitLayer.setMap(null);
  }

  public isWorkflowLayerSection(layer: Layer): boolean {
    if (layer.type === LayerTypes.Alert) {
      const section = FolderUtils.getLayerSection(layer, this.mapDrawingStateService.mapState.foldersSet);

      // has configured widget settings to workflow or layer
      if (!!this.mapDrawingStateService.mapState.sectionWidgetUserSettings[section?.uId]) {
        return this.mapDrawingStateService.mapState.sectionWidgetUserSettings[section?.uId]?.viewMode === 'workflow-step';
      } else {
        // no configured widget settings, use default configuration
        const configuration = !!section?.configuration ? JSON.parse(section?.configuration) : null;
        return configuration?.viewMode === 'workflow';
      }
    }
    return false;
  }

  // returns geo features for document that can be used for zoom
  public getDocumentMainGeoValueFeatures(layer: Layer, dataset: Dataset, document: Document): any[] {
    // for tropical cyclons should use the last track feature
    if (dataset?.subtype == DatasetSubtype.TropicalCyclone) {
      const trackFieldValue = document.track;

      const geoFeatures = trackFieldValue
        ? GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(JSON.parse(trackFieldValue))
        : null;
      if (document.is_active && geoFeatures?.length > 0) {
        const cycloneGeofeature = geoFeatures[geoFeatures.length - 1];
        return [cycloneGeofeature];
      }
    }

    // for other types, just use the document geo features
    const geoFieldNames = MapLayerQueryBuildService.getDatasetGeoFieldNames(layer, dataset);

    if (!(geoFieldNames.length > 0)) {
      return null;
    }

    // Uses the geo selected in the layer settings, else it uses the default geometry from the dataset schema, else whatever other geometry field
    let layerGeoField = layer?.displayOptions?.geoFieldsToDisplay?.length > 0 ? layer?.displayOptions?.geoFieldsToDisplay[0] : null;

    const geoField =
      dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry && f.systemName === layerGeoField) ??
      dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry && f.settings?.defaultGeometry === true) ??
      dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry);

    const geoJsonData = document[geoField.systemName];

    let geoJson;
    try {
      geoJson = JSON.parse(geoJsonData);
    } catch {
      return null;
    }

    return GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(geoJson);
  }

  startWeatherAnimations(): void {
    if (!!!this.mapDrawingStateService.googleMap) {
      return;
    }

    //this.aerisWeatherController.playAnimations();
  }

  resetWeatherAnimations(): void {
    if (!!!this.mapDrawingStateService.googleMap) {
      return;
    }

    //this.aerisWeatherController.resetAnimations();
  }
}
